import {
  Component,
  Input,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MaskPipe } from 'ngx-mask';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ApprovalService } from 'src/app/services/approval/approval.service';

@Component({
  selector: 'app-dashboard-approval-accompaniment',
  templateUrl: './dashboard-approval-accompaniment.component.html',
  styleUrls: ['./dashboard-approval-accompaniment.component.scss'],
})
export class DashboardApprovalAccompanimentComponent
  implements OnInit, OnDestroy, OnChanges
{
  @Input() comparingVersions: boolean = false;

  // Dados para versão principal
  mainData: Partial<UserApprovalSignature>[] = [];
  mainLoading = false;

  // Dados para versão comparando
  comparingData: Partial<UserApprovalSignature>[] = [];
  comparingLoading = false;
  comparingDataLoading = false;

  // Dados normais (sem comparação)
  data: Partial<UserApprovalSignature>[] = [];

  loading = true;

  eventMainVersion: any;
  eventComparingVersion: any;


  constructor(
    private router: Router,
    private api: ApiService,
    private route: ActivatedRoute,
    private authService: AuthService,
    public approvalService: ApprovalService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // Detectar quando comparingVersions muda de true para false
    if (
      changes['comparingVersions'] &&
      !changes['comparingVersions'].firstChange &&
      changes['comparingVersions'].previousValue === true &&
      changes['comparingVersions'].currentValue === false
    ) {
      // Recarregar dados para visualização normal
      if (this.approvalService.approvalData.history) {
        this.data = this.approvalService.approvalData.history;
        this.loading = false;
      } else {
        this.getApprovalData();
      }
    }
  }

  ngOnInit(): void {
    if (this.comparingVersions) {
      this.loadComparingVersions();
    } else {
      if (this.approvalService.approvalData.history) {
        this.data = this.approvalService.approvalData.history;
        this.loading = false;
      } else {
        this.getApprovalData();
      }
    }

    this.eventMainVersion = this.approvalService.changeMainVersion.subscribe(
      () => {
        if (this.comparingVersions) {
          this.loadMainVersion();
        }
      }
    );

    this.eventComparingVersion =
      this.approvalService.changeComparingVersion.subscribe(() => {
        if (this.comparingVersions) {
          this.loadComparingVersion();
        }
      });
  }

  ngOnDestroy(): void {
    if (this.eventMainVersion) {
      this.eventMainVersion.unsubscribe();
    }
    if (this.eventComparingVersion) {
      this.eventComparingVersion.unsubscribe();
    }
  }

  loadComparingVersions() {
    // Só carregar se houver versões selecionadas
    if (this.approvalService.approvalData.mainVersion) {
      this.loadMainVersion();
    }
    if (this.approvalService.approvalData.comparingVersion) {
      this.loadComparingVersion();
    }
  }

  loadMainVersion() {
    if (!this.approvalService.approvalData.mainVersion) {
      this.mainData = [];
      this.mainLoading = false;
      return;
    }
    this.mainLoading = true;
    this.getApprovalData('main');
  }

  loadComparingVersion() {
    if (!this.approvalService.approvalData.comparingVersion) {
      this.comparingData = [];
      this.comparingDataLoading = false;
      this.comparingLoading = false;
      return;
    }
    this.comparingDataLoading = true;
    this.comparingLoading = true;
    this.getApprovalData('comparing');
  }

  // Função para comparar os dados e aplicar classes de comparação
  async compareData() {
    try {
      this.comparingDataLoading = true;
      this.cdr.detectChanges();

      if (!this.mainData || !this.comparingData) {
        this.comparingDataLoading = false;
        this.comparingLoading = false;
        return;
      }

      const processedData: any[] = [];

      // Processar itens da tabela de comparação (direita)
      this.comparingData.forEach((comparingItem: any) => {
        const referenceItem = this.mainData.find(
          (ref: any) => ref.id === comparingItem.id
        );

        if (referenceItem) {
          // Item existe em ambas as tabelas - verificar se há diferenças
          const changedFields: any = {};
          let hasChanges = false;

          // Comparar campos específicos
          if (referenceItem.approved !== comparingItem.approved) {
            changedFields.action = true;
            hasChanges = true;
          }
          if (referenceItem.restart !== comparingItem.restart) {
            changedFields.action = true;
            hasChanges = true;
          }
          if (referenceItem.date !== comparingItem.date) {
            changedFields.date = true;
            hasChanges = true;
          }
          if (referenceItem.reason !== comparingItem.reason) {
            changedFields.reason = true;
            hasChanges = true;
          }
          if (
            referenceItem.department_approval?.id !==
            comparingItem.department_approval?.id
          ) {
            changedFields.phase = true;
            hasChanges = true;
          }

          // Comparar register (usuário)
          const mainIsPerson = referenceItem.register?.person !== null;
          const comparingIsPerson = comparingItem.register?.person !== null;

          if (mainIsPerson !== comparingIsPerson) {
            changedFields.user = true;
            hasChanges = true;
          } else {
            if (mainIsPerson) {
              if (
                referenceItem.register?.person?.full_name !==
                comparingItem.register?.person?.full_name
              ) {
                changedFields.user = true;
                hasChanges = true;
              }
            } else {
              if (
                referenceItem.register?.company?.corporate_name !==
                comparingItem.register?.company?.corporate_name
              ) {
                changedFields.user = true;
                hasChanges = true;
              }
            }
          }

          processedData.push({
            ...comparingItem,
            compareClass: hasChanges ? 'row-compare-modified' : '',
            changedFields: hasChanges ? changedFields : {},
          });
        } else {
          // Item novo (existe só na direita)
          processedData.push({
            ...comparingItem,
            compareClass: 'row-compare-added',
            changedFields: {},
          });
        }
      });

      // Adicionar itens removidos (existem só na esquerda)
      this.mainData.forEach((referenceItem: any) => {
        const existsInComparing = this.comparingData.find(
          (comp: any) => comp.id === referenceItem.id
        );

        if (!existsInComparing) {
          // Item removido - adicionar na lista de comparação com classe removed
          processedData.push({
            ...referenceItem,
            compareClass: 'row-compare-removed',
            changedFields: {},
          });
        }
      });

      this.comparingData = processedData;
      this.comparingDataLoading = false;
      this.comparingLoading = false;
      this.cdr.detectChanges();
    } catch (error) {
      this.comparingDataLoading = false;
      this.comparingLoading = false;
    }
  }

  async getApprovalData(versionType: 'main' | 'comparing' | null = null) {
    // Não chamar APIs v2 - apenas verificar cache
    // Se não houver dados, não fazer nada e não mostrar loading
    if (versionType === 'main') {
      this.mainData = [];
      this.mainLoading = false;
      this.cdr.detectChanges();
    } else if (versionType === 'comparing') {
      this.comparingData = [];
      this.comparingLoading = false;
      this.comparingDataLoading = false;
      this.cdr.detectChanges();
    } else {
      this.data = [];
      this.loading = false;
    }
    return;
    
    /* Código antigo comentado - não chamar APIs v2
    try {
      const req_params: any = {
        register: this.route.snapshot.params.id,
        tab: 'history',
      };

      if (
        versionType === 'main' &&
        this.approvalService.approvalData.mainVersion
      ) {
        req_params['version'] = this.approvalService.approvalData.mainVersion;
      } else if (
        versionType === 'comparing' &&
        this.approvalService.approvalData.comparingVersion
      ) {
        req_params['version'] =
          this.approvalService.approvalData.comparingVersion;
      } else if (!versionType && this.approvalService.approvalData.version) {
        req_params['version'] = this.approvalService.approvalData.version;
      }

      // Se estiver em modo de comparação e não houver versão selecionada, não fazer requisição
      if (
        this.comparingVersions &&
        versionType &&
        ((versionType === 'main' &&
          !this.approvalService.approvalData.mainVersion) ||
          (versionType === 'comparing' &&
            !this.approvalService.approvalData.comparingVersion))
      ) {
        if (versionType === 'main') {
          this.mainData = [];
          this.mainLoading = false;
        } else if (versionType === 'comparing') {
          this.comparingData = [];
          this.comparingLoading = false;
        }
        return;
      }

      // Se estiver em modo de comparação mas não tiver parâmetros de versão, não fazer requisição
      if (this.comparingVersions && versionType && !req_params['version']) {
        if (versionType === 'main') {
          this.mainData = [];
          this.mainLoading = false;
        } else if (versionType === 'comparing') {
          this.comparingData = [];
          this.comparingLoading = false;
        }
        return;
      }

      const data = await this.api.get<any[]>({
        route: `api/v2/approvals/version/`,
        token: true,
        params: req_params,
      });

      if (versionType === 'main') {
        this.mainData = data[0].register.signatures;
        this.mainLoading = false;
        // Se já temos comparing data, fazer comparação
        if (this.comparingVersions && this.comparingData) {
          await this.compareData();
        }
      } else if (versionType === 'comparing') {
        this.comparingData = data[0].register.signatures;
        this.comparingLoading = false;
        this.comparingDataLoading = false;
        // Se já temos main data, fazer comparação
        if (this.comparingVersions && this.mainData) {
          await this.compareData();
        }
      } else {
        this.approvalService.approvalData.history = data[0].register.signatures;
        this.data = data[0].register.signatures;
        this.loading = false;
      }
    } catch (error) {
      if (versionType === 'main') {
        this.mainLoading = false;
      } else if (versionType === 'comparing') {
        this.comparingLoading = false;
        this.comparingDataLoading = false;
      } else {
        this.loading = false;
      }
    }
    */
  }

  handleText(text: string) {
    if (text.length > 40) {
      const shortText = text.substring(0, 40);
      return `${shortText}...`;
    }
    return text;
  }

  redirectTo(path: string) {
    this.router.navigateByUrl(path);
  }

  formateDate(date: string) {
    const day = date.split('T').shift()?.split('-').reverse().join('/');
    const hours = date.split('T').pop()?.slice(0, 5);

    return `${day} às ${hours}`;
  }

  // Funções auxiliares para formatação
  getPhaseName(item: Partial<UserApprovalSignature>): string {
    if (!item.department_approval) return '-';
    if (item.department_approval.type === 'department') {
      return item.department_approval.department?.name || '-';
    }
    return item.department_approval.agent?.name || '-';
  }

  getUserName(item: Partial<UserApprovalSignature>): string {
    if (!item.register) return '-';
    if (item.register.person) {
      return item.register.person.full_name || '-';
    }
    return item.register.company?.corporate_name || '-';
  }

  getAction(item: Partial<UserApprovalSignature>): string {
    if (item.approved) return 'Aprovado';
    if (item.restart) return 'Retornado';
    return 'Reprovado';
  }
}
